import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Toolbar, Item } from "devextreme-react/data-grid";
import GridCreator from "@helpers/GridCreator/GridCreator";
import { basicSettings } from "@helpers/GridCreator/gridConfig";
import { Household, GridType } from "@/types/types";
import { HouseholdSFDS } from "@datasources/HouseholdSFDS";
import useHeaderChangeNoUP from "@/hooks/useHeaderChangeNoUP";
import { HOUSEHOLD_GRID_NO_DATA_MSG } from "@/types/enums";
import { ColumnCellTemplateData } from "devextreme/ui/data_grid";
import { IS_HEROKU } from "@/constants/constants";

export default function HouseholdDictionary() {
  useHeaderChangeNoUP("Household");
  const [errorMsg, setErrorMsg] = useState<
    string | undefined | HOUSEHOLD_GRID_NO_DATA_MSG | string[]
  >(HOUSEHOLD_GRID_NO_DATA_MSG.QueryTooShort);

  const ds = useMemo(() => HouseholdSFDS({ setErrorMsg }), []);

  const gridSettings: GridType<Household, string> = {
    ...basicSettings,
    id: "hhGrid",
    dataSource: ds,
    toolbar: renderToolbar,
    noDataText: Array.isArray(errorMsg) ? errorMsg.join(", ") : errorMsg,
    columns: [
      {
        key: "cmd",
        width: 60,
        cellRender: cmdCellRender,
        allowExporting: false,
        allowHiding: false,
        allowResizing: false,
        showInColumnChooser: false,
      },
      {
        key: "externalCode",
        dataField: "externalCode",
        visible: false,
        allowEditing: false,
        allowFiltering: false,
      },
      {
        key: "name",
        dataType: "string",
        dataField: "name",
        visible: true,
        allowEditing: false,
        filterOperations: ["contains", "<>", "notcontains", "="],
        //temporary contains only to - ADD more filters to SOQL on backend
      },
      {
        key: "clientCode",
        dataType: "string",
        dataField: "clientCode",
        visible: true,
        allowEditing: false,
        caption: "Client Code",
        filterOperations: ["contains", "<>", "notcontains", "="],
        //temporary contains only to - ADD more filters to SOQL on backend
      },
      {
        key: "primaryContactPreferredName",
        dataType: "string",
        dataField: "primaryContact.preferredFullName",
        visible: true,
        allowEditing: false,
        caption: "Preferred Name",
        filterOperations: ["contains", "<>", "notcontains", "="],
        //temporary contains only to - ADD more filters to SOQL on backend
      },
      {
        key: "primaryContactBirthDate",
        dataType: "date",
        dataField: "primaryContact.birthDate",
        visible: true,
        allowEditing: false,
        caption: "Birth Date",
        allowFiltering: false,
        // temporary disable date filtering to sort out the SOQL query to SF
        // filterOperations: ["between", "<>", "<", "<=", ">", ">=", "="],
      },
      {
        key: "secondaryContactPreferredName",
        dataType: "string",
        dataField: "secondaryContact.preferredFullName",
        visible: true,
        allowEditing: false,
        caption: "Preferred Name II",
        filterOperations: ["contains", "<>", "notcontains", "="],
        //temporary contains only to - ADD more filters to SOQL on backend
      },
      {
        key: "secondaryContactBirthDate",
        dataType: "date",
        dataField: "secondaryContact.birthDate",
        visible: true,
        allowEditing: false,
        caption: "Birth Date II",
        allowFiltering: false,
        // temporary disable date filtering to sort out the SOQL query to SF
        // filterOperations: ["between", "<>", "<", "<=", ">", ">=", "="],
      },
    ],
    headerFilter: {
      visible: false,
    },
  };

  return <GridCreator {...gridSettings} />;
}
function renderToolbar() {
  return (
    <Toolbar>
      <Item name="columnChooserButton" location="after" />
      <Item name="searchPanel" location="after" />
    </Toolbar>
  );
}

function cmdCellRender(e: ColumnCellTemplateData<Household>) {
  if (!IS_HEROKU && e.row.data.isHeroku) {
    return <div className="font-semibold">HEROKU</div>;
  } else if (IS_HEROKU && !e.row.data.isHeroku) {
    return <div className="font-semibold">AZURE</div>;
  }

  return (
    <div className="flex items-center justify-center">
      <Link to={`/household/${e.row.data.externalCode}/balance-sheet`}>
        <img src="/icons/eye-icon.svg" alt="" />
      </Link>
    </div>
  );
}
